import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  ViewList as ViewListIcon,
  AccountTree as AccountTreeIcon,
} from '@mui/icons-material';
import { useModal } from '../../contexts/ModalContext';
import { useAuth } from '../../contexts/AuthContext';
import { useAccount } from '../../contexts/AccountContext';

// Import our new components
import teamService from '../../services/teamService';
import TeamHierarchyVisualizer from '../../components/teams/TeamHierarchyVisualizer';
import TeamCreationWizard from '../../components/teams/TeamCreationWizard';
import TeamDetail from '../../components/teams/TeamDetail';

export const Teams = ({ teams, users, onDelete, onEdit, onManageMembers, onRefresh, onCreateTeam }) => {
  const [viewMode, setViewMode] = useState('hierarchical'); // 'hierarchical' or 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const { openWarningModal } = useModal();
  const { user, loading } = useAuth();
  const { accountId } = useAccount();

  // Effect to listen for team selection events from child components
  useEffect(() => {
    const handleTeamSelect = (event) => {
      const { team } = event.detail;
      if (team) {
        setSelectedTeam(team);
      }
    };

    // Add event listener for the custom event
    window.addEventListener('selectTeam', handleTeamSelect);

    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('selectTeam', handleTeamSelect);
    };
  }, []);

  // Add a loading state to prevent actions before user data is available
  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>Loading team data...</Box>;
  }
  
  // Function to handle team selection for detailed view
  const handleTeamSelect = (team) => {
    setSelectedTeam(team);
  };

  // Function to handle team creation
  const handleCreateTeam = async (teamData) => {
    try {
      // Call the parent component's onCreateTeam function
      const result = await onCreateTeam(teamData);
      
      if (result === true) {
        // Close dialog and refresh data
        setOpenCreateDialog(false);
        return true;
      } else {
        // If there was an error, return it to display in the form
        return result;
      }
    } catch (error) {
      console.error('Error creating team:', error);
      return false;
    }
  };

  // Function to handle team editing
  const handleEditTeam = (teamId, teamData) => {
    return onEdit(teamId, teamData);
  };

  // Function to handle adding a member to a team
  const handleAddMember = async (teamId, userId) => {
    try {
      console.log(`Adding user ${userId} to team ${teamId}`);
      // Here you would call your API to add the member
      // After successful addition, refresh the data
      await teamService.addUserToTeam(accountId, teamId, userId);
      await onRefresh();
      return true;
    } catch (error) {
      console.error('Error adding team member:', error);
      return false;
    }
  };

  // Function to handle removing a member from a team
  const handleRemoveMember = async (accountId, teamId, userId) => {
    try {
      console.log(`Removing user ${userId} from team ${teamId}`);
      
      // Use the team service's existing method for removing members
      await teamService.removeUserFromTeam(accountId, teamId, userId);
      
      // After successful removal, refresh the data
      await onRefresh();
      return true;
    } catch (error) {
      console.error('Error removing team member:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to remove team member';
      openWarningModal(errorMessage); // Display error to user
      return false;
    }
  };

  // Filter teams based on search and team level
  const filteredTeams = teams.filter(team => {
    const lowerCaseQuery = searchQuery.toLowerCase();
    
    const matchesName = !searchQuery || 
      team.name.toLowerCase().includes(lowerCaseQuery);
      
    const matchesTag = !searchQuery || 
      (Array.isArray(team.tags) && 
       team.tags.some(tag => tag.toLowerCase().includes(lowerCaseQuery)));
    
    const matchesLevel = true; // No longer filtering by level, so always true
    
    return (matchesName || matchesTag) && matchesLevel;
  });

  // If a team is selected, show its detail view
  if (selectedTeam) {
    return (
      <TeamDetail
        team={selectedTeam}
        teams={teams}
        users={users}
        onClose={() => setSelectedTeam(null)}
        onEdit={handleEditTeam}
        onAddMember={handleAddMember}
        onRemoveMember={handleRemoveMember}
        userRole={user?.role}
      />
    );
  }

  return (
    <Box>
      {/* Header and Controls */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Team Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenCreateDialog(true)}
        >
          Create Team
        </Button>
      </Box>

      {/* Filters and View Toggle */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Search Teams"
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            <Tabs
              value={viewMode}
              onChange={(e, newValue) => setViewMode(newValue)}
              sx={{ display: { xs: 'flex', sm: 'inline-flex' } }}
            >
              <Tab
                value="hierarchical"
                label="Hierarchy"
                icon={<AccountTreeIcon />}
                iconPosition="start"
              />
              <Tab
                value="list"
                label="List"
                icon={<ViewListIcon />}
                iconPosition="start"
              />
            </Tabs>
          </Grid>
        </Grid>
      </Paper>

      {/* Team Visualization - Either Hierarchical or List */}
      {viewMode === 'hierarchical' ? (
        <TeamHierarchyVisualizer
          teams={filteredTeams}
          users={users}
          onEditTeam={(team) => handleTeamSelect(team)}
          onDeleteTeam={onDelete}
          onManageTeamMembers={(teamId) => {
            const team = teams.find(t => t.id === teamId);
            if (team) {
              handleTeamSelect(team);
            }
          }}
          userRole={user?.role}
        />
      ) : (
        <Paper sx={{ width: '100%', mb: 2, p: 2 }}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6">Teams List View</Typography>
          </Box>
          
          {/* Legacy Teams List View - Integrated from original component */}
          <Grid container spacing={2}>
            {filteredTeams.map(team => {
              const teamUsers = users.filter(user => user.team_id === team.id);
              const teamManager = users.find(user => user.id === team.manager);
              
              return (
                <Grid item xs={12} sm={6} md={4}>
                  <Paper elevation={1} sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" gutterBottom>
                        {team.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {teamManager ? `Manager: ${teamManager.name}${teamManager.designation ? ` (${teamManager.designation})` : ''}` : 'Manager: No Manager'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Members: {team.members_count}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Sub-Teams: {teams.filter(t => t.parent_team === team.id).length}
                      </Typography>
                    </Box>
                    
                    {team.tags && Array.isArray(team.tags) && team.tags.length > 0 && (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                        {team.tags.map((tag, index) => (
                          tag.includes(',') ?
                            tag.split(',').map((subTag, subIndex) => (
                              <Chip 
                                key={`${index}-${subIndex}`} 
                                label={subTag.trim()} 
                                size="small" 
                                variant="outlined" 
                                color="primary"
                                sx={{ mb: 0.5 }}
                              />
                            ))
                          :
                          <Chip 
                            key={index} 
                            label={tag} 
                            size="small" 
                            variant="outlined" 
                            color="primary"
                            sx={{ mb: 0.5 }}
                          />
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Grid>
              );
            })}
            
            {filteredTeams.length === 0 && (
              <Grid item xs={12}>
                <Box sx={{ textAlign: 'center', p: 3 }}>
                  <Typography variant="body1" color="text.secondary">
                    No teams match your search criteria.
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}

      {/* Team Creation Wizard Dialog */}
      <TeamCreationWizard
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        onSubmit={handleCreateTeam}
        teams={teams}
        users={users}
      />
    </Box>
  );
};

export default Teams;